export type MailErrorArea = "mail" | "mail-calendar";

export type MailErrorAction =
    | "load-mailboxes"
    | "load-folders"
    | "load-list"
    | "load-detail"
    | "recognize"
    | "load-conversation"
    | "load-inline-images"
    | "load-attachments"
    | "download-attachment"
    | "download-mail"
    | "save-attachment"
    | "send"
    | "post-send"
    | "save-draft"
    | "sync"
    | "search"
    | "load-more"
    | "mark-read"
    | "mark-unread"
    | "move"
    | "archive"
    | "delete"
    | "restore"
    | "assign"
    | "link"
    | "load-mentions"
    | "load-linked-mails"
    | "load-calendar"
    | "save-calendar"
    | "load-settings"
    | "save-settings"
    | "delete-settings"
    | "print";

export type MailErrorKind = "http" | "network" | "timeout" | "unknown";

export type MailErrorInput = {
    area: MailErrorArea;
    action: MailErrorAction;
    error: unknown;
    fallbackMessage: string;
};

export type MailErrorLogEntry = {
    event: "mail_frontend_error";
    area: MailErrorArea;
    action: MailErrorAction;
    kind: MailErrorKind;
    status?: number;
    occurredAt: string;
};

function errorName(error: unknown): string {
    if (error instanceof Error) return error.name;
    if (typeof error === "object" && error !== null && "name" in error) {
        return String(error.name);
    }
    return "";
}

function errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    return "";
}

function httpStatus(error: unknown): number | undefined {
    if (typeof error === "object" && error !== null && "status" in error) {
        const status = Number(error.status);
        if (Number.isInteger(status) && status >= 100 && status <= 599) return status;
    }
    const match = errorMessage(error).match(/^\s*([1-5]\d{2})\b/);
    return match ? Number(match[1]) : undefined;
}

function classify(error: unknown, status: number | undefined): MailErrorKind {
    const name = errorName(error);
    const message = errorMessage(error);
    if (
        name === "AbortError" ||
        name === "TimeoutError" ||
        /\b(?:timeout|timed out|aborted)\b/i.test(message)
    ) {
        return "timeout";
    }
    if (status !== undefined) return "http";
    if (error instanceof TypeError || /(?:failed to fetch|network error|netzwerkfehler)/i.test(message)) {
        return "network";
    }
    return "unknown";
}

/**
 * Baut ausschließlich aus einer festen Feld-Whitelist einen Log-Eintrag.
 * Der Rohfehler wird bewusst nicht übernommen: Fehlermeldungen von Graph und
 * Backend können Mailbox-Adressen, Betreffzeilen oder Graph-IDs enthalten.
 */
export function toMailErrorLog(input: MailErrorInput, now = new Date()): MailErrorLogEntry {
    const status = httpStatus(input.error);
    return {
        event: "mail_frontend_error",
        area: input.area,
        action: input.action,
        kind: classify(input.error, status),
        ...(status === undefined ? {} : { status }),
        occurredAt: now.toISOString(),
    };
}

/**
 * T-45: Meldung, wenn der VERSAND in die Zeitüberschreitung läuft. Ein Abbruch
 * auf unserer Seite heißt nicht, dass der Server aufgehört hat — auf PROD war
 * die Mail in genau diesem Fall raus. „Fehlgeschlagen, bitte erneut versuchen"
 * führte dann zu doppelten Mails.
 */
export const MAIL_SEND_TIMEOUT_MESSAGE =
    "Der Versand dauert ungewöhnlich lange und ist vermutlich durchgegangen. " +
    "Bitte nicht erneut senden, sondern unter „Gesendete Elemente“ prüfen.";

/**
 * T-340: Der Ausgang ließ sich nicht feststellen — weder der Server kannte den
 * Versand noch (soweit nachsehbar) „Gesendete Elemente“. Nur dann darf die
 * Oberfläche wieder zum Senden einladen; der Server sieht bei der Wiederholung
 * mit derselben Kennung vorher nach, ob die Mail schon raus ist.
 */
export const MAIL_SEND_UNBEKANNT_MESSAGE =
    "KMVS konnte nicht feststellen, ob die Mail gesendet wurde. Bitte unter „Gesendete Elemente“ nachsehen. " +
    "Fehlt sie dort, kann sie erneut freigegeben werden – KMVS prüft vor dem Senden, ob sie schon raus ist.";

/**
 * T-340: Meldung für einen Versand, den der Server als FEHLGESCHLAGEN kennt
 * (Statusabfrage). Hier ist sicher: Die Mail ist nicht raus.
 */
export function versandFehlgeschlagenMeldung(fehler?: { status: number; message: string } | null): string {
    const basis = "Die Mail wurde nicht gesendet.";
    if (!fehler) return `${basis} Bitte erneut versuchen.`;
    const alsFehler = new Error(
        `${fehler.status} Fehler: ${JSON.stringify({ statusCode: fehler.status, message: fehler.message })}`,
    );
    const detail = describeMailClientError(alsFehler);
    return detail ? `${basis} ${detail}` : `${basis} Bitte erneut versuchen.`;
}

/** true, wenn der Fehler eine Zeitüberschreitung (Abbruch) ist. */
export function isMailTimeoutError(error: unknown): boolean {
    return classify(error, httpStatus(error)) === "timeout";
}

/**
 * Gateway-Status, bei denen der Proxy (nginx) aufgegeben hat, das Backend aber
 * womöglich weiterarbeitet: 502 Bad Gateway, 504 Gateway Timeout. 500 und 503
 * kommen dagegen vom Backend selbst bzw. bedeuten „nicht angenommen" — die
 * bleiben echte Fehler.
 */
const GATEWAY_STATUS_UNGEWISS = new Set([502, 504]);

/**
 * T-45: Ist nach diesem Fehler OFFEN, ob der Versand durchging? Gilt für eine
 * eigene Zeitüberschreitung und für 502/504 vom Proxy (nginx kappt auf PROD
 * nach 75 s). Dann darf die Oberfläche nicht zum erneuten Senden einladen.
 */
export function isMailSendOutcomeUnknown(error: unknown): boolean {
    const status = httpStatus(error);
    if (status !== undefined && GATEWAY_STATUS_UNGEWISS.has(status)) return true;
    return classify(error, status) === "timeout";
}

/** Feldnamen der Mail-DTOs, wie die Oberfläche sie zeigt. */
const FELD_ANZEIGE: Record<string, string> = { to: "An", cc: "Cc", bcc: "Bcc" };

/**
 * Bekannte Server-Meldungen (class-validator, Nest, Express) → Deutsch. Die
 * Reihenfolge zählt: die erste passende Regel gewinnt.
 */
const SERVER_MELDUNG_DEUTSCH: ReadonlyArray<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/^(?:each value in )?(to|cc|bcc) must be an email$/i,
        (m) => `Mindestens eine Adresse unter ${FELD_ANZEIGE[m[1].toLowerCase()]} ist keine gültige E-Mail-Adresse.`],
    [/^to (?:should not be empty|must contain at least 1 elements|must be an array)$/i,
        () => "Bitte mindestens einen Empfänger angeben."],
    [/^(to|cc|bcc) must contain no more than (\d+) elements$/i,
        (m) => `Unter ${FELD_ANZEIGE[m[1].toLowerCase()]} sind höchstens ${m[2]} Empfänger möglich.`],
    [/^(cc|bcc) must be an array$/i,
        (m) => `Die Empfänger unter ${FELD_ANZEIGE[m[1].toLowerCase()]} sind ungültig.`],
    [/^mailbox must be an email$/i,
        () => "Das Absender-Postfach ist keine gültige E-Mail-Adresse."],
    [/^subject must be (?:longer than or equal to 1 characters|a string)$/i,
        () => "Bitte einen Betreff eingeben."],
    [/^subject must be shorter than or equal to (\d+) characters$/i,
        (m) => `Der Betreff ist zu lang (max. ${m[1]} Zeichen).`],
    [/^attachments must contain no more than (\d+) elements$/i,
        (m) => `Zu viele Anhänge (max. ${m[1]}).`],
    [/^attachments\.\d+\.contentBytes must be shorter than or equal to \d+ characters$/i,
        () => "Ein Anhang ist zu groß."],
    [/^attachments\.\d+\.contentType must be one of the following values/i,
        () => "Ein Anhang hat einen Dateityp, der nicht versendet werden darf."],
    [/^request entity too large$/i,
        () => "Die Nachricht ist zu groß (inkl. Anhänge und eingebetteter Bilder)."],
    [/^forbidden resource$/i,
        () => "Dafür fehlt die Berechtigung."],
    [/^unauthorized$/i,
        () => "Die Anmeldung ist abgelaufen. Bitte neu anmelden."],
];

/** Übersetzt eine einzelne Server-Meldung; Unbekanntes bleibt wörtlich stehen. */
export function translateMailServerMessage(message: string): string {
    const text = message.trim();
    for (const [muster, deutsch] of SERVER_MELDUNG_DEUTSCH) {
        const m = text.match(muster);
        if (m) return deutsch(m);
    }
    return text;
}

/** JSON-Teil von „409 Conflict: {…}" — `null`, wenn es keiner ist. */
function serverBody(error: unknown): Record<string, unknown> | null {
    const roh = errorMessage(error).replace(/^\s*[1-5]\d{2}\b[^:{]*:\s*/, "");
    try {
        const body: unknown = JSON.parse(roh);
        return typeof body === "object" && body !== null && !Array.isArray(body)
            ? (body as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
}

/**
 * T-240: Maschinenlesbarer `code` aus der Server-Antwort (z. B.
 * `MAIL_CAPTURE_PENDING` vom Sicherungs-Guard) — sonst `null`.
 */
export function extractMailServerCode(error: unknown): string | null {
    if (httpStatus(error) === undefined) return null;
    const code = serverBody(error)?.code;
    return typeof code === "string" && code.trim() ? code.trim() : null;
}

/** T-240: Text, wenn die Sicherung auch nach dem Warten noch läuft. */
export const MAIL_CAPTURE_PENDING_MESSAGE =
    "Die Mail wird noch gesichert und ist deshalb noch nicht archiviert. Zuordnungen bleiben bestehen — bitte in einer Minute erneut archivieren.";

/**
 * Liest die Meldung(en) aus dem Fehlertext, den `fetchJson` baut
 * („400 Bad Request: {…}"). Nest liefert `message` als String oder als Liste
 * (class-validator). Kein JSON (z. B. eine HTML-Seite vom Proxy) → nichts,
 * dann bleibt es beim allgemeinen Text.
 */
export function extractMailServerMessages(error: unknown): string[] {
    const body = serverBody(error);
    if (body === null || !("message" in body)) return [];
    const msg = (body as { message: unknown }).message;
    const liste = Array.isArray(msg) ? msg : [msg];
    return liste
        .filter((m): m is string => typeof m === "string" && m.trim() !== "")
        .map((m) => m.trim());
}

/**
 * Lesbare, möglichst deutsche Beschreibung eines 4xx-Fehlers aus der
 * Server-Antwort — oder `null`, wenn der Fehler keine solche Meldung trägt.
 * Für 413 gibt es eine eigene Meldung, auch ohne JSON-Antwort (nginx).
 */
export function describeMailClientError(error: unknown): string | null {
    const status = httpStatus(error);
    if (status === undefined || status < 400 || status > 499) return null;
    if (status === 413) return translateMailServerMessage("request entity too large");
    const meldungen = extractMailServerMessages(error).map(translateMailServerMessage);
    const eindeutig = Array.from(new Set(meldungen));
    return eindeutig.length ? eindeutig.join(" ") : null;
}

function userMessage(input: MailErrorInput, entry: MailErrorLogEntry): string {
    if (extractMailServerCode(input.error) === "MAIL_CAPTURE_PENDING") return MAIL_CAPTURE_PENDING_MESSAGE;
    if (
        input.action === "send" &&
        (entry.kind === "timeout" ||
            (entry.status !== undefined && GATEWAY_STATUS_UNGEWISS.has(entry.status)))
    ) {
        return MAIL_SEND_TIMEOUT_MESSAGE;
    }
    if (input.action === "send" && entry.kind === "http") {
        const detail = describeMailClientError(input.error);
        if (detail) return `${input.fallbackMessage.replace(/[.:]\s*$/, "")}: ${detail}`;
    }
    if (entry.kind === "network") {
        return `${input.fallbackMessage} Bitte Verbindung prüfen und erneut versuchen.`;
    }
    if (entry.kind === "timeout") {
        return `${input.fallbackMessage} Bitte erneut versuchen.`;
    }
    return input.fallbackMessage;
}

/**
 * Zentrale Naht für Mail-Frontendfehler. Der Transport bleibt hier gekapselt,
 * damit später dauerhaftes Backend-Logging ergänzt werden kann, ohne die
 * aufrufenden Komponenten erneut zu ändern.
 */
export function reportMailError(input: MailErrorInput): string {
    const entry = toMailErrorLog(input);
    console.error("[MailError]", entry);
    return userMessage(input, entry);
}
